<template>
	<div class="input-group grow" :class="inputGroupClasses">
		<app-label v-if="label" :field="id ?? name" :inline="inline" data-testid="label">{{
			label
		}}</app-label>
		<div :class="{ 'mt-2': label, 'sm:grow sm:mt-0': inline }">
			<div :class="wrapperClasses" data-testid="wrapper">
				<span
					v-if="prefix"
					data-testid="prefix"
					class="flex select-none items-center pl-3 text-gray-500 sm:text-sm"
					>{{ prefix }}</span
				>
				<textarea
					v-if="type == 'textarea'"
					:id="id ?? name"
					:name="name"
					:aria-label="name"
					:autocomplete="autocomplete"
					:class="fieldClasses"
					:placeholder="placeholder"
					:disabled="disabled"
					:value="modelValue"
					:rows="rows"
					data-testid="input"
					@input="updateValue"></textarea>

				<input
					v-else
					:id="id ?? name"
					:type="type"
					:name="name"
					:aria-label="name"
					:autocomplete="autocomplete"
					:class="fieldClasses"
					:disabled="disabled"
					:placeholder="placeholder"
					:value="modelValue"
					data-testid="input"
					@input="updateValue" />

				<div
					v-if="error"
					data-testid="error-icon"
					class="pointer-events-none absolute inset-y-0 right-0 flex pr-3"
					:class="{
						'items-center': type != 'textarea',
						'mt-2.5': type == 'textarea'
					}">
					<font-awesome-icon
						icon="fas fa-exclamation-circle"
						class="text-red-600"></font-awesome-icon>
				</div>
			</div>

			<p
				v-if="error"
				:id="`${name}-error`"
				data-testid="error-message"
				class="mt-2 text-sm text-red-600">
				{{ error }}
			</p>

			<p
				v-else-if="hint"
				:id="`${name}-description`"
				data-testid="hint"
				class="mt-2 text-sm text-gray-500">
				{{ hint }}
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { useFieldClasses } from '@/composables/forms'

	const props = withDefaults(
		defineProps<{
			modelValue?: any
			id?: string
			label?: string
			prefix?: string
			name?: string
			hint?: string
			error?: string
			disabled?: boolean
			autocomplete?: string
			placeholder?: string
			inline?: boolean
			rows?: number
			type?: 'text' | 'textarea' | 'email' | 'phone' | 'number' | 'password'
			size?: 'small' | 'medium' | 'large'
			theme?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger'
		}>(),
		{
			modelValue: undefined,
			id: '',
			label: undefined,
			prefix: undefined,
			name: '',
			hint: undefined,
			error: undefined,
			placeholder: '',
			theme: 'primary',
			size: 'medium',
			type: 'text',
			autocomplete: 'on',
			inline: false,
			rows: 5
		}
	)

	// Styles
	const { wrapperClasses, inputGroupClasses, fieldClasses } = useFieldClasses(props)

	// Events
	const emit = defineEmits(['update:modelValue'])
	function updateValue(e: Event) {
		emit('update:modelValue', (e.target as HTMLInputElement).value)
	}
</script>
